/**
 * Mock data cho Credit System
 * Dùng khi chưa có DB thật (dev mode)
 * Replace bằng Supabase queries khi production
 */

import type { CreditPack, CreditTransaction, CreditBalance } from '../../types/credit';

// =====================
// Credit Packs (pricing catalog)
// =====================

export const CREDIT_PACKS: CreditPack[] = [
  {
    id: 'pack-trial',
    name: 'Gói Thử',
    credits: 100_000,
    bonusCredits: 0,
    totalCredits: 100_000,
    priceVnd: 19_000,
    pricePerCredit: 0.19,
    savingsPercent: 0,
    isPopular: false,
    description: '1 báo cáo dày ~25 trang HOẶC 5-7 file ngắn',
  },
  {
    id: 'pack-small',
    name: 'Gói Nhỏ',
    credits: 500_000,
    bonusCredits: 0,
    totalCredits: 500_000,
    priceVnd: 79_000,
    pricePerCredit: 0.158,
    savingsPercent: 17,
    isPopular: true,
    description: '5-6 báo cáo dày HOẶC 25-35 file ngắn',
  },
  {
    id: 'pack-medium',
    name: 'Gói Vừa',
    credits: 1_500_000,
    bonusCredits: 0,
    totalCredits: 1_500_000,
    priceVnd: 199_000,
    pricePerCredit: 0.133,
    savingsPercent: 30,
    isPopular: false,
    description: '15-20 báo cáo dày HOẶC 70-100 file ngắn',
  },
  {
    id: 'pack-large',
    name: 'Gói Lớn',
    credits: 5_000_000,
    bonusCredits: 0,
    totalCredits: 5_000_000,
    priceVnd: 599_000,
    pricePerCredit: 0.12,
    savingsPercent: 37,
    isPopular: false,
    description: '50-65 báo cáo dày HOẶC 250-330 file ngắn',
  },
];

// =====================
// Free tier config
// =====================

export const FREE_CREDITS = {
  welcome: 0,           // signup chưa cho gì
  emailVerify: 0,       // email verify cũng chưa
  phoneVerify: 200_000, // verify phone → nhận 200K credits (đủ 1 báo cáo dày complete) — DEC-BIZ-2026-04-30-01
  phoneVerifyExpiryDays: 30, // welcome credit hết hạn 30 ngày → ép activation sớm
  referral: 50_000,     // mời bạn
  referralPurchase: 100_000, // bạn mua pack → cả 2 nhận
} as const;

// =====================
// Mock balance (cho dev)
// =====================

let mockBalance: CreditBalance = {
  credits: 200_000,
  creditsUsed: 0,
  creditsPurchased: 0,
};

let mockTransactions: CreditTransaction[] = [
  {
    id: 'tx-welcome',
    userId: 'mock-user',
    type: 'welcome',
    credits: 200_000,
    balanceAfter: 200_000,
    reason: 'Phone verified — 200K credits miễn phí (đủ 1 báo cáo dày)',
    createdAt: new Date().toISOString(),
  },
];

// =====================
// Mock functions
// =====================

export function getMockBalance(): CreditBalance {
  return { ...mockBalance };
}

export function getMockTransactions(): CreditTransaction[] {
  return [...mockTransactions];
}

export function getMockPacks(): CreditPack[] {
  return [...CREDIT_PACKS];
}

export function mockCheckCredits(tokensNeeded: number): {
  allowed: boolean;
  creditsNeeded: number;
  creditsAvailable: number;
  creditsAfter: number;
  reason?: string;
} {
  const available = mockBalance.credits;
  const allowed = available >= tokensNeeded;
  return {
    allowed,
    creditsNeeded: tokensNeeded,
    creditsAvailable: available,
    creditsAfter: allowed ? available - tokensNeeded : available,
    reason: allowed ? undefined : `Cần ${tokensNeeded.toLocaleString()} credits, chỉ còn ${available.toLocaleString()}`,
  };
}

export function mockDeductCredits(tokens: number, reason: string, jobId?: string): CreditBalance {
  mockBalance = {
    ...mockBalance,
    credits: Math.max(0, mockBalance.credits - tokens),
    creditsUsed: mockBalance.creditsUsed + tokens,
  };

  mockTransactions.unshift({
    id: `tx-${Date.now()}`,
    userId: 'mock-user',
    type: 'consume',
    credits: -tokens,
    balanceAfter: mockBalance.credits,
    reason,
    relatedJobId: jobId,
    createdAt: new Date().toISOString(),
  });

  return { ...mockBalance };
}

export function mockAddCredits(tokens: number, type: 'purchase' | 'welcome' | 'bonus' | 'refund', reason: string, purchaseId?: string): CreditBalance {
  mockBalance = {
    ...mockBalance,
    credits: mockBalance.credits + tokens,
    creditsPurchased: mockBalance.creditsPurchased + tokens,
  };

  mockTransactions.unshift({
    id: `tx-${Date.now()}`,
    userId: 'mock-user',
    type,
    credits: tokens,
    balanceAfter: mockBalance.credits,
    reason,
    relatedPurchaseId: purchaseId,
    createdAt: new Date().toISOString(),
  });

  return { ...mockBalance };
}

export function mockPurchasePack(packId: string): CreditBalance | null {
  const pack = CREDIT_PACKS.find(p => p.id === packId);
  if (!pack) return null;

  return mockAddCredits(
    pack.totalCredits,
    'purchase',
    `Mua ${pack.name} (${pack.priceVnd.toLocaleString()}đ)`,
    `purchase-${Date.now()}`
  );
}

export function mockResetBalance(): void {
  mockBalance = { credits: 0, creditsUsed: 0, creditsPurchased: 0 };
  mockTransactions = [];
}

// =====================
// Token estimation helpers
// =====================

/**
 * Ước tính tokens từ text segments
 * Vietnamese: ~2.5 chars per token
 * Output: ~1.3x input (AI tends to generate slightly more)
 */
export function estimateTokensFromSegments(segments: string[]): {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
} {
  const totalChars = segments.reduce((sum, s) => sum + s.length, 0);
  const inputTokens = Math.ceil(totalChars / 2.5);
  const outputTokens = Math.ceil(inputTokens * 1.3);
  return {
    inputTokens,
    outputTokens,
    totalTokens: inputTokens + outputTokens,
  };
}

/**
 * Ước tính "tương đương bao nhiêu trang" cho display.
 * Anchor (real measure 2026-04-27): 1 trang báo cáo/audit/FDI dày ≈ 3,700 total tokens
 * (input ~1,500 + output ~2,200 với output_ratio 1.3).
 * File ngắn (CV/letter) ≈ 1,000-1,500 token/trang — user sẽ dịch nhiều trang hơn estimate.
 * Per DEC-BIZ-2026-04-30-01 pricing strategy lock.
 */
export function tokensToPages(totalTokens: number): number {
  return Math.max(1, Math.ceil(totalTokens / 3_700));
}

/**
 * Format số credits cho display — full number với thousand separator.
 * VD: 52400 → "52,400" (không phải "52.4" hoặc "52K" — tránh user hiểu nhầm).
 */
export function formatCredits(credits: number): string {
  return credits.toLocaleString('en-US');
}

/**
 * Format giá VND
 */
export function formatVnd(amount: number): string {
  return amount.toLocaleString('vi-VN') + 'đ';
}
